import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  CssBaseline,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Toolbar,
  Typography,
  styled,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import {
  createFlowSegmentationAssignment,
  getSegmentationFormCatalog,
  listStepConnectionsByFlowRole,
} from "./whatsappSegmentationApi";
import { FLOW_ROLE_OPTIONS } from "./whatsappFlowCatalogs";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const ButtonPrincipal = styled(Button)({
  borderRadius: "20px",
  backgroundColor: "#161e55",
  color: "#ffff",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#A8C8AF",
  },
  "&:disabled": {
    backgroundColor: "rgba(22, 30, 85, 0.4)",
    color: "#fff",
  },
});

const ButtonSecondary = styled(Button)({
  borderRadius: "20px",
  color: "#161e55",
  borderColor: "#161e55",
  textTransform: "none",
});

const flowLabel = (flow) => {
  const name = flow?.name != null ? String(flow.name).trim() : "";
  return name !== "" ? name : flow?.uuid ?? "—";
};

const centeredSpinnerBoxSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "calc(100vh - 120px)",
  width: "100%",
};

const initialFormState = () => ({
  flowRole: "",
  stepConnectionUuid: "",
  selectedCommerces: [],
  selectedClientTypes: [],
  scoreMin: "",
  scoreMax: "",
});

const SCORE_PAIR_INCOMPLETE =
  "Completa el score mínimo y el score máximo, o deja ambos vacíos.";
const SCORE_NOT_INTEGER =
  "El score mínimo y el score máximo deben ser números enteros mayores o iguales a 0.";
const SCORE_MIN_GREATER =
  "El score mínimo no puede ser mayor que el score máximo.";

const parseScoreInput = (value) => {
  const trimmed = String(value ?? "").trim();
  if (trimmed === "") return { kind: "empty" };
  if (!/^\d+$/.test(trimmed)) return { kind: "invalid" };
  const number = Number(trimmed);
  if (!Number.isSafeInteger(number)) return { kind: "invalid" };
  return { kind: "value", value: number };
};

const validateScorePair = (scoreMin, scoreMax) => {
  const min = parseScoreInput(scoreMin);
  const max = parseScoreInput(scoreMax);
  if (min.kind === "invalid" || max.kind === "invalid") return SCORE_NOT_INTEGER;
  if (min.kind === "empty" && max.kind === "empty") return null;
  if (min.kind === "empty" || max.kind === "empty") return SCORE_PAIR_INCOMPLETE;
  if (min.value > max.value) return SCORE_MIN_GREATER;
  return null;
};

const scorePayload = (scoreMin, scoreMax) => {
  const min = parseScoreInput(scoreMin);
  const max = parseScoreInput(scoreMax);
  if (min.kind !== "value" || max.kind !== "value") return {};
  return { scoreMin: min.value, scoreMax: max.value };
};

const compareEs = (a, b) => String(a).localeCompare(String(b), "es");

const sortCommercesByName = (list) =>
  [...list].sort((a, b) => a.name.localeCompare(b.name, "es"));

const sortStringsEs = (list) => [...list].sort(compareEs);

const sortFlowsByName = (list) =>
  [...list].sort((a, b) => {
    const nameA = flowLabel(a);
    const nameB = flowLabel(b);
    return nameA.localeCompare(nameB, "es");
  });

const filterCommerces = (options, { inputValue }) => {
  const q = inputValue.trim().toLowerCase();
  const filtered = !q
    ? options
    : options.filter((o) => o.name.toLowerCase().includes(q));
  return sortCommercesByName(filtered);
};

function SectionPanel({ title, children }) {
  return (
    <Box>
      <Typography component="h2" variant="h6" sx={{ color: "#161e55", fontWeight: 600 }}>
        {title}
      </Typography>
      <Box
        sx={{
          border: "1px solid #161e55",
          borderRadius: "1rem",
          overflow: "auto",
          mt: 2,
          p: 2,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function SelectedItemsBelow({ label, items, onRemove }) {
  if (!items.length) return null;
  const sortedItems = [...items].sort((a, b) =>
    a.label.localeCompare(b.label, "es")
  );
  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid rgba(22, 30, 85, 0.15)" }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {sortedItems.map((item) => (
          <Chip
            key={item.key}
            label={item.label}
            size="small"
            onDelete={() => onRemove(item.key)}
            sx={{
              color: "#161e55",
              borderColor: "#161e55",
              "& .MuiChip-deleteIcon": { color: "#161e55" },
            }}
            variant="outlined"
          />
        ))}
      </Stack>
    </Box>
  );
}

export default function FlujosWhatsappSegmentacionNueva() {
  const navigate = useNavigate();

  const [commerces, setCommerces] = useState([]);
  const [typeClientOptions, setTypeClientOptions] = useState([]);
  const [flows, setFlows] = useState([]);
  const [form, setForm] = useState(initialFormState);

  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [loadingFlows, setLoadingFlows] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [duplicateMessage, setDuplicateMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingCatalog(true);
    setError(null);
    getSegmentationFormCatalog()
      .then((catalog) => {
        if (!cancelled) {
          setCommerces(sortCommercesByName(catalog.commerces));
          setTypeClientOptions(sortStringsEs(catalog.typeClient));
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e?.message || "No se pudo cargar el catálogo del formulario.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCatalog(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchFlows = useCallback(async (role) => {
    setLoadingFlows(true);
    setError(null);
    try {
      const data = await listStepConnectionsByFlowRole(role);
      setFlows(sortFlowsByName(data));
    } catch (e) {
      setError(e?.message || "No se pudo cargar la lista de flujos.");
      setFlows([]);
    } finally {
      setLoadingFlows(false);
    }
  }, []);

  useEffect(() => {
    if (!form.flowRole) {
      setFlows([]);
      setForm((prev) => ({ ...prev, stepConnectionUuid: "" }));
      return;
    }
    setForm((prev) => ({ ...prev, stepConnectionUuid: "" }));
    fetchFlows(form.flowRole);
  }, [form.flowRole, fetchFlows]);

  const flowSelectPlaceholder = !form.flowRole
    ? "Primero elige el tipo"
    : loadingFlows
      ? "Cargando flujos…"
      : "Seleccionar flujo";

  const validateForm = () => {
    if (!form.flowRole) return "Selecciona el tipo de flujo.";
    if (!form.stepConnectionUuid) return "Selecciona un flujo.";
    const scoreError = validateScorePair(form.scoreMin, form.scoreMax);
    if (scoreError) return scoreError;
    const hasScore = Object.keys(scorePayload(form.scoreMin, form.scoreMax)).length > 0;
    if (
      form.selectedCommerces.length === 0 &&
      form.selectedClientTypes.length === 0 &&
      !hasScore
    ) {
      return "Indica al menos un comercio, un tipo de cliente o un rango de score.";
    }
    return null;
  };

  const resetForm = () => {
    setForm(initialFormState());
    setFlows([]);
    setDuplicateMessage(null);
  };

  const handleCommercesChange = (_, newValue, reason, details) => {
    if (details?.option) {
      const exists = form.selectedCommerces.some(
        (c) => c.uuid === details.option.uuid
      );
      if (
        exists &&
        (reason === "selectOption" || reason === "removeOption")
      ) {
        setDuplicateMessage("Este comercio ya está seleccionado.");
        return;
      }
    }

    if (newValue.length < form.selectedCommerces.length) {
      setDuplicateMessage("Este comercio ya está seleccionado.");
      return;
    }

    setDuplicateMessage(null);
    setForm((prev) => ({
      ...prev,
      selectedCommerces: sortCommercesByName(newValue),
    }));
  };

  const handleClientTypesChange = (event) => {
    const value = event.target.value;
    const next = typeof value === "string" ? value.split(",") : value;
    const prev = form.selectedClientTypes;

    if (next.length < prev.length) {
      setDuplicateMessage("Este tipo de cliente ya está seleccionado.");
      return;
    }

    if (next.length > prev.length) {
      const added = next.filter((t) => !prev.includes(t));
      if (added.length === 0) {
        setDuplicateMessage("Este tipo de cliente ya está seleccionado.");
        return;
      }
    }

    setDuplicateMessage(null);
    setForm((prev) => ({
      ...prev,
      selectedClientTypes: sortStringsEs(next),
    }));
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await createFlowSegmentationAssignment({
        stepConnectionUuid: form.stepConnectionUuid,
        commerces: form.selectedCommerces.map((c) => c.uuid),
        typeClient: form.selectedClientTypes,
        ...scorePayload(form.scoreMin, form.scoreMax),
      });
      setSuccessMessage("Asignación creada correctamente.");
      resetForm();
    } catch (e) {
      const apiMessage = e?.response?.data?.message;
      setError(apiMessage || e?.message || "No se pudo guardar la asignación.");
    } finally {
      setSaving(false);
    }
  };

  if (loadingCatalog) {
    return (
      <React.Fragment>
        <CssBaseline />
        <Toolbar />
        <Box sx={centeredSpinnerBoxSx}>
          <CircularProgress sx={{ color: "#161e55" }} />
        </Box>
        <Toolbar />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <CssBaseline />
      <Toolbar />
      <Box sx={{ width: "100%", margin: "18px 14px" }}>
        <Stack spacing={2}>
          <Item>.</Item>
          <Item></Item>
          <Grid container alignItems="center">
            <Grid item xs={12}>
              <Box
                mt={2}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box>
                  <h1 style={{ margin: 0, color: "#161e55" }}>Nueva asignación</h1>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Completa el tipo de flujo y el flujo. Indica al menos un comercio, un tipo de cliente o un rango de score.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <ButtonSecondary
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/homeAdmin/flujos-whatsapp/segmentaciones")}
                  >
                    Volver
                  </ButtonSecondary>
                  <ButtonPrincipal
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Guardando…" : "Guardar"}
                  </ButtonPrincipal>
                </Stack>
              </Box>
            </Grid>
          </Grid>

          <Stack spacing={3} sx={{ width: "100%" }}>
            <SectionPanel title="Tipo de flujo">
              <FormControl fullWidth size="small" disabled={loadingFlows}>
                <InputLabel id="create-flow-role-label">Tipo de flujo</InputLabel>
                <Select
                  labelId="create-flow-role-label"
                  label="Tipo de flujo"
                  value={form.flowRole}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, flowRole: e.target.value }))
                  }
                >
                  <MenuItem value="">
                    <em>Seleccionar</em>
                  </MenuItem>
                  {FLOW_ROLE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </SectionPanel>

            <SectionPanel title="Flujo">
              <FormControl
                fullWidth
                size="small"
                disabled={
                  !form.flowRole ||
                  loadingFlows ||
                  (form.flowRole && !loadingFlows && flows.length === 0)
                }
              >
                <InputLabel id="create-flow-select-label">Flujo</InputLabel>
                <Select
                  labelId="create-flow-select-label"
                  label="Flujo"
                  value={form.stepConnectionUuid}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      stepConnectionUuid: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="">
                    <em>{flowSelectPlaceholder}</em>
                  </MenuItem>
                  {!loadingFlows &&
                    flows.map((flow, index) => (
                      <MenuItem
                        key={flow.uuid || `flow-${index}`}
                        value={flow.uuid}
                      >
                        {flowLabel(flow)}
                      </MenuItem>
                    ))}
                </Select>
                {form.flowRole && loadingFlows ? (
                  <FormHelperText>Cargando listado de flujos…</FormHelperText>
                ) : null}
                {form.flowRole && !loadingFlows && flows.length === 0 ? (
                  <FormHelperText>No hay flujos para este tipo.</FormHelperText>
                ) : null}
              </FormControl>
            </SectionPanel>

            <SectionPanel title="Comercios">
              <Autocomplete
                multiple
                fullWidth
                openOnFocus
                disableCloseOnSelect
                options={commerces}
                value={form.selectedCommerces}
                onChange={handleCommercesChange}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                filterOptions={filterCommerces}
                ListboxProps={{ style: { maxHeight: 280 } }}
                noOptionsText="No hay comercios que coincidan con el filtro"
                renderTags={() => null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Comercios"
                    placeholder="Escriba para filtrar o seleccione"
                  />
                )}
              />
              <SelectedItemsBelow
                label="Comercios seleccionados"
                items={form.selectedCommerces.map((c) => ({
                  key: c.uuid,
                  label: c.name,
                }))}
                onRemove={(uuid) =>
                  setForm((prev) => ({
                    ...prev,
                    selectedCommerces: prev.selectedCommerces.filter(
                      (c) => c.uuid !== uuid
                    ),
                  }))
                }
              />
            </SectionPanel>

            <SectionPanel title="Tipo de cliente">
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel id="create-client-types-label" shrink>
                  Tipo de cliente
                </InputLabel>
                <Select
                  labelId="create-client-types-label"
                  label="Tipo de cliente"
                  multiple
                  displayEmpty
                  notched
                  value={form.selectedClientTypes}
                  onChange={handleClientTypesChange}
                  renderValue={() => (
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                    >
                      Seleccionar tipo de cliente
                    </Typography>
                  )}
                >
                  {typeClientOptions.map((type) => (
                    <MenuItem
                      key={type}
                      value={type}
                      onClick={(event) => {
                        if (form.selectedClientTypes.includes(type)) {
                          event.preventDefault();
                          event.stopPropagation();
                          setDuplicateMessage(
                            "Este tipo de cliente ya está seleccionado."
                          );
                        }
                      }}
                    >
                      {type}
                    </MenuItem>
                  ))}
                </Select>
                {typeClientOptions.length === 0 ? (
                  <FormHelperText>No hay tipos de cliente en el catálogo.</FormHelperText>
                ) : null}
              </FormControl>
              <SelectedItemsBelow
                label="Tipos de cliente seleccionados"
                items={form.selectedClientTypes.map((type) => ({
                  key: type,
                  label: type,
                }))}
                onRemove={(type) =>
                  setForm((prev) => ({
                    ...prev,
                    selectedClientTypes: prev.selectedClientTypes.filter(
                      (t) => t !== type
                    ),
                  }))
                }
              />
            </SectionPanel>

            <SectionPanel title="Score">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Score mínimo"
                    value={form.scoreMin}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        scoreMin: event.target.value,
                      }))
                    }
                    inputProps={{ inputMode: "numeric", "aria-label": "Score mínimo" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Score máximo"
                    value={form.scoreMax}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        scoreMax: event.target.value,
                      }))
                    }
                    inputProps={{ inputMode: "numeric", "aria-label": "Score máximo" }}
                  />
                </Grid>
              </Grid>
              <FormHelperText sx={{ mx: 0, mt: 1 }}>
                Puedes guardar solo con el rango de score. Si lo indicas, completa el mínimo y el máximo.
              </FormHelperText>
            </SectionPanel>
          </Stack>
        </Stack>
      </Box>
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={5000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)} sx={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(duplicateMessage)}
        autoHideDuration={4000}
        onClose={() => setDuplicateMessage(null)}
      >
        <Alert
          severity="warning"
          onClose={() => setDuplicateMessage(null)}
          sx={{ width: "100%" }}
        >
          {duplicateMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ width: "100%" }}>
          {successMessage}
        </Alert>
      </Snackbar>
      <Toolbar />
    </React.Fragment>
  );
};
